/**
 * Feb 2026 — Per-partner LocalBusiness JSON-LD builder for the
 * PWS Partner Water Testing landing page.
 *
 * Extracted from `PwsPartnerWaterTestingPreview.jsx` so we can unit-
 * test the schema shape in isolation. Google gates local-search
 * eligibility on this schema being valid + stable — one bad emit
 * costs suburb-level visibility for weeks.
 *
 * Contract:
 *   • Returns `null` when we don't have enough data to build a
 *     credible LocalBusiness node (no business_name OR no canonical
 *     URL). The caller MUST skip `upsertJsonLd` on null.
 *   • Never emits blank `areaServed` — the site-wide LocalBusiness
 *     already covers Australia-wide; a partner node without `areaServed`
 *     would confuse Google about coverage.
 *   • De-duplicates `areaServed` (case-insensitive) so the region
 *     label appearing again inside `service_suburbs` doesn't double
 *     up as a Place node.
 *   • Parents to the site-wide Organization node via `@id` — this
 *     is what lets Google collapse the entity graph and attribute
 *     reviews / rankings to the same brand.
 */

const PARENT_ORG_ID = "https://awarewatertesting.com.au/#organization";

export function buildPartnerLocalBusinessLd({
  businessName,
  canonicalUrl,
  regionLabel,
  serviceSuburbs,
  phoneDisplay,
  emailDisplay,
  franchiseePhotoUrl,
} = {}) {
  if (!businessName || !canonicalUrl) return null;

  // Build a de-duplicated, case-insensitive `areaServed` list.
  // Region first (broader), then each suburb.
  const seen = new Set();
  const areaServed = [];
  const push = (raw) => {
    const name = (raw || "").trim();
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    areaServed.push({ "@type": "Place", name });
  };
  push(regionLabel);
  (serviceSuburbs || []).forEach(push);

  // If we've got literally zero areas, refuse to emit — a partner
  // LocalBusiness with no coverage doesn't help SEO and can hurt
  // entity resolution.
  if (areaServed.length === 0) return null;

  const node = {
    "@context": "https://schema.org",
    "@type":    ["LocalBusiness", "TestingLaboratory"],
    "@id":      `${canonicalUrl}#partner`,
    name:       businessName,
    url:        canonicalUrl,
    parentOrganization: { "@id": PARENT_ORG_ID },
    areaServed,
  };
  if (phoneDisplay)        node.telephone = phoneDisplay;
  if (emailDisplay)        node.email     = emailDisplay;
  if (franchiseePhotoUrl)  node.image     = franchiseePhotoUrl;
  return node;
}
